import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarRange, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Local date as YYYY-MM-DD — never via UTC, which shifts the day either side. */
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return iso(d);
}

const QUICK = [
  { key: "7", label: "Last 7 days", range: () => ({ from: daysAgo(6), to: iso(new Date()) }) },
  { key: "30", label: "Last 30 days", range: () => ({ from: daysAgo(29), to: iso(new Date()) }) },
  { key: "90", label: "Last 90 days", range: () => ({ from: daysAgo(89), to: iso(new Date()) }) },
  {
    key: "month",
    label: "This month",
    range: () => {
      const n = new Date();
      return { from: iso(new Date(n.getFullYear(), n.getMonth(), 1)), to: iso(n) };
    },
  },
  {
    // The one people actually ask for — "how did we do last month".
    key: "lastmonth",
    label: "Last month",
    range: () => {
      const n = new Date();
      return {
        from: iso(new Date(n.getFullYear(), n.getMonth() - 1, 1)),
        to: iso(new Date(n.getFullYear(), n.getMonth(), 0)),
      };
    },
  },
  {
    key: "year",
    label: "This year",
    range: () => {
      const n = new Date();
      return { from: iso(new Date(n.getFullYear(), 0, 1)), to: iso(n) };
    },
  },
  { key: "all", label: "All time", range: () => ({ from: "", to: "" }) },
];

/** Human wording for whatever is chosen, for the button and the chip. */
export function describeRange({ from, to }) {
  if (!from && !to) return "All time";
  const match = QUICK.find((q) => {
    const r = q.range();
    return r.from === from && r.to === to;
  });
  if (match && match.key !== "all") return match.label;
  const short = (d) =>
    new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
  if (from && to) return `${short(from)} – ${short(to)}`;
  return from ? `From ${short(from)}` : `Until ${short(to)}`;
}

/** Whether a record's date falls inside the range. Blank means no limit. */
export function withinRange(value, { from, to }) {
  if (!value) return !from && !to;
  const d = String(value).slice(0, 10);
  if (from && d < from) return false;
  if (to && d > to) return false;
  return true;
}

export const ALL_TIME = { from: "", to: "" };

/** " · last 30 days" for a subtitle, so a total says what period it covers. */
export function rangeNote(range) {
  if (!range.from && !range.to) return "";
  return ` · ${describeRange(range).toLowerCase()}`;
}

export default function DateRange({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => new Date());
  // Held separately so nothing changes until Apply — half a range shouldn't
  // filter the list.
  const [draft, setDraft] = useState(value);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    const key = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const cells = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const total = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const lead = first.getDay();
    return [
      ...Array.from({ length: lead }, () => null),
      ...Array.from({ length: total }, (_, i) =>
        iso(new Date(month.getFullYear(), month.getMonth(), i + 1))
      ),
    ];
  }, [month]);

  const pick = (day) => {
    // First tap sets the start; the second closes the range, whichever way round.
    if (!draft.from || (draft.from && draft.to)) {
      setDraft({ from: day, to: "" });
    } else if (day < draft.from) {
      setDraft({ from: day, to: draft.from });
    } else {
      setDraft({ ...draft, to: day });
    }
  };

  const active = !!(value.from || value.to);

  return (
    <div className="relative" ref={box}>
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "h-9 inline-flex items-center gap-2 px-3 rounded-lg border text-[12.5px] whitespace-nowrap transition-colors",
          active ? "border-brand text-brand bg-brand-light/40" : "border-line text-ink hover:border-muted2"
        )}
      >
        <CalendarRange className="w-3.5 h-3.5" />
        {describeRange(value)}
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 flex rounded-2xl border border-line bg-white shadow-lift overflow-hidden">
          <div className="w-[142px] bg-surface border-r border-line py-2.5 shrink-0">
            <div className="eyebrow px-4 pb-2">Quick ranges</div>
            {QUICK.map((q) => {
              const r = q.range();
              const on = r.from === draft.from && r.to === draft.to;
              return (
                <button
                  key={q.key}
                  onClick={() => {
                    onChange(r);
                    setOpen(false);
                  }}
                  className={cn(
                    "w-full text-left px-4 py-2 text-[12.5px] transition-colors",
                    on ? "bg-brand-light text-brand font-medium" : "text-muted2 hover:text-ink"
                  )}
                >
                  {q.label}
                </button>
              );
            })}
          </div>

          <div className="p-4 w-[268px]">
            <div className="flex items-center mb-3">
              <button
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                aria-label="Previous month"
                className="p-1 text-muted2 hover:text-ink"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="flex-1 text-center font-display text-[14.5px] font-semibold">
                {MONTHS[month.getMonth()]} {month.getFullYear()}
              </span>
              <button
                onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                aria-label="Next month"
                className="p-1 text-muted2 hover:text-ink"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5">
              {DAYS.map((d, i) => (
                <span
                  key={i}
                  className="text-center font-mono text-[9.5px] tracking-wide text-muted2 pb-1.5"
                >
                  {d}
                </span>
              ))}
              {cells.map((day, i) =>
                day === null ? (
                  <span key={`gap-${i}`} />
                ) : (
                  <button
                    key={day}
                    onClick={() => pick(day)}
                    className={cn(
                      "text-[12px] py-1.5 transition-colors",
                      day === draft.from || day === draft.to
                        ? "bg-brand text-white"
                        : draft.from && draft.to && day > draft.from && day < draft.to
                        ? "bg-brand-light"
                        : "hover:bg-surface",
                      day === draft.from && draft.to && "rounded-l-lg",
                      day === draft.to && "rounded-r-lg",
                      day === draft.from && !draft.to && "rounded-lg",
                      !(day === draft.from || day === draft.to) && "rounded-lg"
                    )}
                  >
                    {Number(day.slice(-2))}
                  </button>
                )
              )}
            </div>

            <div className="flex items-center mt-3 pt-3 border-t border-line">
              <button
                onClick={() => setDraft(ALL_TIME)}
                className="text-[12.5px] text-muted2 underline hover:text-ink"
              >
                Clear
              </button>
              <Button
                size="sm"
                disabled={!!draft.from && !draft.to}
                onClick={() => {
                  onChange(draft);
                  setOpen(false);
                }}
                className="ml-auto bg-brand hover:bg-brand-hover"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Shown beside the search, so a shorter list has a visible reason. */
export function RangeChip({ value, onClear }) {
  if (!value.from && !value.to) return null;
  return (
    <button
      onClick={onClear}
      className="inline-flex items-center gap-2 rounded-full bg-brand-light text-brand px-3 py-1.5 text-[12px] hover:opacity-80 transition-opacity"
    >
      {describeRange(value)}
      <X className="w-3 h-3" />
    </button>
  );
}
